import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from 'antd';
import { EVENTS, SCHEDULE_EVENT_TYPES, SCHEDULE_ANALYTICS_FILTER_TYPE } from 'shared/constants';
import { useEducationalEventsContext, HOSTED_BY, DATES } from '../../educational-events-context';
import { SearchBox, Select } from 'src/components/design-system';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import analyticsService from 'src/helpers/analytics.service';
import { isCurrentUserHost, isUpcomingEvent } from '../../helpers/event-view.helpers';
import { EventListItem } from './event-list-item';
import styles from './events-sidebar.module.scss';

const SKELETON_COUNT = 4;
const LOAD_MORE_THRESHOLD_PX = 300;
// Rows the filtered list should hold before we stop auto-pulling pages.
const MIN_FILLED_ROWS = 20;

export function EventsSidebar() {
  const {
    schedulesLang,
    loading,
    events,
    userId,
    canManageEvents,
    selectedEventId,
    setSelectedEventId,
    hasMoreEvents,
    loadingMoreEvents,
    loadMoreEvents,
    loadMoreEventsError,
  } = useEducationalEventsContext();

  const [searchTerm, setSearchTerm] = useState('');
  const [hostedBy, setHostedBy] = useState(HOSTED_BY.ALL);
  // null = auto: default to All when a past event is selected, else Upcoming. An
  // explicit user choice wins from then on (derived so no effect / stale render).
  const [dates, setDates] = useState(null);

  const effectiveHostedBy = canManageEvents ? hostedBy : HOSTED_BY.ALL;

  const selectedIsHidden = useMemo(() => {
    const selected = events.find(event => event.id === selectedEventId);
    return !!selected && !isUpcomingEvent(selected);
  }, [events, selectedEventId]);
  const effectiveDates = dates ?? (selectedIsHidden ? DATES.ALL : DATES.UPCOMING);

  const filteredEvents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return events.filter(event => {
      if (term && !event.title?.toLowerCase().includes(term)) return false;
      if (effectiveHostedBy === HOSTED_BY.MINE && !isCurrentUserHost({ event, userId }))
        return false;
      if (effectiveDates === DATES.UPCOMING && !isUpcomingEvent(event)) return false;
      return true;
    });
  }, [events, searchTerm, effectiveHostedBy, effectiveDates, userId]);

  // Scroll the selected event to the top on selection change. A ResizeObserver
  // re-aligns as the list grows to its final height, then latches + disconnects.
  const selectedItemRef = useRef(null);
  const scrolledToRef = useRef(null);
  useLayoutEffect(() => {
    if (scrolledToRef.current === selectedEventId) return;
    const item = selectedItemRef.current;
    const container = item?.closest(`.${styles.list}`);
    if (!item || !container) return;

    const align = () => {
      // Offsets are unreliable until the list is actually scrollable.
      if (container.scrollHeight <= container.clientHeight) return;
      container.scrollTop = item.offsetTop - container.offsetTop;
      scrolledToRef.current = selectedEventId;
      observer.disconnect();
    };

    const observer = new ResizeObserver(align);
    observer.observe(container);
    align();
    return () => observer.disconnect();
  }, [selectedEventId]);

  const handleSelectEvent = event => {
    analyticsService.track(EVENTS.SCHEDULE.OPEN_EVENT, {
      eventId: event.id,
      eventTitle: event.title,
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
    });
    setSelectedEventId(event.id);
  };

  const handleHostedByChange = value => {
    setHostedBy(value);
    analyticsService.track(EVENTS.SCHEDULE.FILTER, {
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      filterType: SCHEDULE_ANALYTICS_FILTER_TYPE.HOSTED_BY,
      value,
    });
  };

  const handleDatesChange = value => {
    setDates(value);
    analyticsService.track(EVENTS.SCHEDULE.FILTER, {
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      filterType: SCHEDULE_ANALYTICS_FILTER_TYPE.DATES,
      value,
    });
  };

  const hostedByOptions = [
    { value: HOSTED_BY.ALL, label: schedulesLang.FILTER_ALL_HOSTS },
    { value: HOSTED_BY.MINE, label: schedulesLang.FILTER_EVENTS_I_HOST },
  ];

  const datesOptions = [
    { value: DATES.ALL, label: schedulesLang.FILTER_ALL_DATES },
    { value: DATES.UPCOMING, label: schedulesLang.FILTER_UPCOMING },
  ];

  // loadMoreEvents no-ops while a page is in flight or nothing is left.
  const handleListScroll = event => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX) loadMoreEvents();
  };

  // Search and the filters only see loaded pages, and a list too short to
  // scroll can never hit the scroll trigger — keep pulling while a search is
  // active or the filtered list hasn't filled, so unloaded matches stay
  // reachable. Stops on a load-more failure (scrolling retries it).
  useEffect(() => {
    if (loading || !hasMoreEvents || loadingMoreEvents || loadMoreEventsError) return;
    if (searchTerm.trim() || filteredEvents.length < MIN_FILLED_ROWS) loadMoreEvents();
  }, [
    loading,
    hasMoreEvents,
    loadingMoreEvents,
    loadMoreEventsError,
    searchTerm,
    filteredEvents.length,
    loadMoreEvents,
  ]);

  const renderList = () => {
    if (loading) {
      return (
        <div className={styles.list}>
          {Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <div key={index} className={styles.skeletonItem}>
              <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 1, width: '90%' }} />
            </div>
          ))}
        </div>
      );
    }

    if (!filteredEvents.length) {
      return <p className={styles.empty}>{schedulesLang.NO_EVENTS}</p>;
    }

    return (
      <ListItemsNavigator
        className={styles.list}
        orientation="vertical"
        onScroll={handleListScroll}>
        {filteredEvents.map(event => {
          const isSelected = event.id === selectedEventId;
          return (
            <EventListItem
              key={event.id}
              ref={isSelected ? selectedItemRef : undefined}
              event={event}
              isSelected={isSelected}
              onSelect={() => handleSelectEvent(event)}
            />
          );
        })}
        {loadingMoreEvents && (
          // disabled: ListItemsNavigator clones children with nav props; this
          // keeps arrow-key focus off the placeholder row.
          <div className={styles.skeletonItem} disabled aria-hidden>
            <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 1, width: '90%' }} />
          </div>
        )}
      </ListItemsNavigator>
    );
  };

  return (
    <nav className={styles.sidebar} aria-label={schedulesLang.NAV_EDUCATIONAL_EVENTS}>
      <SearchBox
        className={styles.search}
        placeholder={schedulesLang.SEARCH_PLACEHOLDER}
        value={searchTerm}
        onChange={event => setSearchTerm(event.target.value)}
      />
      <div className={styles.filters}>
        {canManageEvents && (
          <Select
            name="hostedBy"
            aria-label={schedulesLang.FILTER_HOSTED_BY}
            value={hostedBy}
            onChange={(_, value) => handleHostedByChange(value)}
            options={hostedByOptions}
            allowClear={false}
            noMarginBottom
          />
        )}
        <Select
          name="dates"
          aria-label={schedulesLang.FILTER_DATES}
          value={effectiveDates}
          onChange={(_, value) => handleDatesChange(value)}
          options={datesOptions}
          allowClear={false}
          noMarginBottom
        />
      </div>
      {renderList()}
    </nav>
  );
}
